import { Alert } from "@mantine/core";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import type { NotificationStreamStatus } from "./streamStatus";
import { useNotificationStreamStore } from "./streamStatus";

// Неблокирующий индикатор состояния SSE-стрима уведомлений.
//
// Подписчик стрима смонтирован на каждой защищённой странице
// (`ProtectedRoute`), поэтому индикатор — не toast (тот всплывал бы
// на каждую неудачную попытку бесконечно), а один спокойный
// `<Alert>`, отрисованный ровно один раз и меняющий вид при смене
// статуса. Статусы `idle`/`live` не показывают ничего — там всё
// в порядке или подписка ещё не запущена.
//
// `unavailable` — компактный однострочный вид (только `title`, без
// `bodyKey`): этот статус выставляется один раз и держится всю
// сессию (реконнект прекращён навсегда), поэтому двухстрочный блок
// висел бы наверху каждого экрана постоянно и на странице очереди
// складывался бы с другими алертами, съедая первый экран. `title`
// самодостаточен («обновления не приходят — обновите страницу»),
// объяснение про сервер опущено. `reconnecting` — короткоживущий,
// оставлен разворотным (title + body).
//
// Без кнопки закрытия: возможность спрятать всё ещё сломанное
// состояние вернула бы ровно ту молчаливость, которую этот индикатор
// чинит.

interface StreamAlertView {
  readonly color: string;
  readonly titleKey: string;
  readonly bodyKey?: string;
}

const ALERT_VIEWS: Partial<Record<NotificationStreamStatus, StreamAlertView>> =
  {
    reconnecting: {
      color: "yellow",
      titleKey: "notifications.stream.reconnectingTitle",
      bodyKey: "notifications.stream.reconnectingBody",
    },
    unavailable: {
      color: "orange",
      titleKey: "notifications.stream.unavailableTitle",
    },
  };

export function StreamAlert(): ReactElement | null {
  const status = useNotificationStreamStore((state) => state.status);
  const { t } = useTranslation();
  const view = ALERT_VIEWS[status];

  if (view === undefined) {
    return null;
  }

  return (
    <Alert variant="light" color={view.color} title={t(view.titleKey)} mb="md">
      {view.bodyKey !== undefined ? t(view.bodyKey) : null}
    </Alert>
  );
}
